<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { SelectButtonPassThroughOptions } from 'primevue/selectbutton'
import Profile from '@/pages/banks/my-bank/Profile.vue'
import FinancingProfile from '@/pages/banks/my-bank/FinancingProfile.vue'
import Policies from '@/pages/banks/my-bank/Policies.vue'
import BorrowerProfiles from '@/pages/banks/my-bank/BorrowerProfiles.vue'
import PartnerBankData = App.Data.PartnerBank.PartnerBankData

const props = defineProps<{
  partnerBank: PartnerBankData
}>()

const { t } = useI18n()

const subtitle = computed(() => [props.partnerBank.bic, props.partnerBank.city].filter(Boolean).join(' · '))

const View = {
  Profile: 'profile',
  Financing: 'financing',
  Policies: 'policies',
  Borrowers: 'borrowers',
} as const

type ViewValue = (typeof View)[keyof typeof View]

const selectedView = ref<ViewValue>(View.Profile)

const viewOptions = computed(() => [
  { label: t('view.profile'), value: View.Profile, icon: 'pi pi-building-columns' },
  { label: t('view.financing'), value: View.Financing, icon: 'pi pi-calculator' },
  { label: t('view.policies'), value: View.Policies, icon: 'pi pi-list-check' },
  { label: t('view.borrowers'), value: View.Borrowers, icon: 'pi pi-id-card' },
])

const viewSelectPt: SelectButtonPassThroughOptions = {
  root: {
    class:
      'shadow-card flex w-full overflow-hidden !rounded-2xl gap-2 p-2 border border-surface-200 bg-surface-0 min-h-20',
  },
  pcToggleButton: {
    root: ({ context }: { context: { active: boolean } }) => ({
      class: [
        'flex-1 !border-0 !rounded-xl',
        !context.active && '!bg-white !text-surface-500 hover:!bg-gray-200',
        context.active && '!bg-success-800 !text-white',
      ],
    }),
    content: () => ({
      class: ['!bg-transparent !shadow-none h-full w-full'],
    }),
  },
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col gap-2">
      <h1 class="text-surface-900 text-2xl font-semibold">
        {{ partnerBank.name || t('my_bank') }}
      </h1>
      <span v-if="subtitle" class="text-surface-500">{{ subtitle }}</span>
    </div>

    <SelectButton
      v-model="selectedView"
      :options="viewOptions"
      option-label="label"
      option-value="value"
      :allow-empty="false"
      class="w-full"
      :pt="viewSelectPt">
      <template #option="{ option }">
        <span class="flex items-center justify-center gap-3">
          <i :class="option.icon" aria-hidden="true" />
          <span>{{ option.label }}</span>
        </span>
      </template>
    </SelectButton>

    <Profile v-if="selectedView === View.Profile" :partner-bank="partnerBank" />
    <FinancingProfile v-else-if="selectedView === View.Financing" :partner-bank="partnerBank" />
    <Policies v-else-if="selectedView === View.Policies" :partner-bank="partnerBank" />
    <BorrowerProfiles v-else :partner-bank="partnerBank" />
  </div>
</template>

<i18n>
{
  "de": {
    "my_bank": "Meine Bank",
    "view": {
      "profile": "Profil",
      "financing": "Finanzierung",
      "policies": "Richtlinien",
      "borrowers": "Kreditnehmer"
    }
  },
  "en": {
    "my_bank": "My Bank",
    "view": {
      "profile": "Profile",
      "financing": "Financing",
      "policies": "Policies",
      "borrowers": "Borrowers"
    }
  }
}
</i18n>
